import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Flame, Timer, Footprints, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassCard } from "@/components/GlassCard";
import { ErrorState } from "@/components/common/ErrorState";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { getChallengesApi, toggleChallengeApi } from "@/lib/api";

const CHALLENGE_ICONS = {
  "streak-7": Flame,
  "weekly-150": Timer,
  "morning-walk": Footprints,
};

// Stable keys for skeleton placeholders (not array indices)
const SKELETON_KEYS = ["skeleton-challenge-a", "skeleton-challenge-b", "skeleton-challenge-c"];

const ChallengeCard = ({ challenge, busy, onToggle }) => {
  const { t } = useLanguage();
  const Icon = CHALLENGE_ICONS[challenge.key] || Flame;

  return (
    <GlassCard className="p-4" data-testid={`community-challenge-${challenge.key}`}>
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-cyan-300/10 border border-cyan-300/25 flex items-center justify-center">
          <Icon size={19} className="text-cyan-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">
              {t(`challengesContent.${challenge.key}.title`)}
            </p>
            {challenge.joined && (
              <span
                data-testid={`community-challenge-joined-${challenge.key}`}
                className="flex items-center gap-1 text-[9px] uppercase tracking-wider rounded-full bg-emerald-400/10 border border-emerald-400/30 px-1.5 py-0.5 text-emerald-300"
              >
                <Check size={9} /> {t("challengesContent.joinedBadge")}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
            {t(`challengesContent.${challenge.key}.desc`)}
          </p>

          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] text-[hsl(var(--muted-foreground))] mb-1.5">
              <span>{t("challengesContent.progressLabel")}</span>
              <span className="[font-variant-numeric:tabular-nums]">
                {challenge.progress}/{challenge.target}{" "}
                {t(`challengesContent.${challenge.key}.unit`)}
              </span>
            </div>
            <Progress value={challenge.progress_pct} className="h-1.5 bg-white/10" />
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
              {t("challengesContent.participants", { n: challenge.participants })}
            </p>
            <Button
              data-testid={`community-challenge-toggle-${challenge.key}`}
              onClick={() => onToggle(challenge)}
              disabled={busy}
              size="sm"
              className={
                challenge.joined
                  ? "rounded-lg h-8 px-4 text-xs font-semibold bg-white/10 text-white hover:bg-white/15 border border-white/10"
                  : "rounded-lg h-8 px-4 text-xs font-semibold bg-cyan-300 text-slate-950 hover:bg-cyan-200"
              }
            >
              {challenge.joined ? t("challengesContent.leave") : t("challengesContent.join")}
            </Button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};

export const ChallengesSection = () => {
  const { profile } = useProfile();
  const { t } = useLanguage();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setChallenges(await getChallengesApi(profile.id));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [profile.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (challenge) => {
    setBusyKey(challenge.key);
    try {
      const res = await toggleChallengeApi(challenge.key, profile.id);
      setChallenges((prev) =>
        prev.map((c) =>
          c.key === challenge.key
            ? { ...c, joined: res.joined, participants: res.participants }
            : c
        )
      );
      toast.success(
        res.joined ? t("challengesContent.joinedToast") : t("challengesContent.leftToast")
      );
    } catch {
      toast.error(t("community.loadError"));
    } finally {
      setBusyKey(null);
    }
  };

  if (error) {
    return (
      <ErrorState
        message={t("community.loadError")}
        onRetry={load}
        testId="community-challenges-retry-button"
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} className="h-36 rounded-2xl bg-white/10" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="community-challenges-list">
      {challenges.map((c) => (
        <ChallengeCard key={c.key} challenge={c} busy={busyKey === c.key} onToggle={handleToggle} />
      ))}
    </div>
  );
};
